import { FC, useEffect, useRef, useState } from 'react'
import { MdFormatColorFill, MdFormatColorText } from 'react-icons/md'
import { Style } from '../../../lib/Types'

type Props = {
  type: 'text' | 'bg'
  isActive: boolean
  backgroundColor?: Style
  toggle: (style: Style, action?: 'add' | 'remove') => void
}

const CellStylingColorPicker: FC<Props> = ({
  type,
  isActive,
  backgroundColor,
  toggle,
}) => {
  const [showColors, toggleColors] = useState(false)
  const colors = ['gray', 'red', 'orange', 'yellow', 'green', 'blue', 'purple']
  const gradients = [100, 300, 500, 700, 900]
  const ref = useRef<HTMLDivElement | null>(null)

  let iconColor = isActive ? 'text-black' : 'text-gray-300'

  if (isActive) {
    iconColor =
      backgroundColor && /700|900|black/.test(backgroundColor)
        ? 'text-white'
        : 'text-black'
  }

  // Close the color selector on click anywhere outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        toggleColors(false)
      }
    }

    if (showColors) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showColors])

  return (
    <div className='relative inline-block ml-3'>
      <div
        className={`${iconColor} ${backgroundColor && backgroundColor.replace('text', 'bg')} px-1 pb-1`}
      >
        {type === 'bg' && (
          <MdFormatColorFill
            className={`cursor-pointer inline-block ${iconColor}`}
            size='1.25rem'
            onClick={() => {
              if (isActive) toggleColors(!showColors)
            }}
          />
        )}
        {type === 'text' && (
          <MdFormatColorText
            className={`cursor-pointer inline-block ${iconColor}`}
            size='1.25rem'
            onClick={() => {
              if (isActive) toggleColors(!showColors)
            }}
          />
        )}
      </div>
      {showColors && (
        <div
          className='flex absolute top-7 left-0 z-100  border-2 bg-white'
          ref={ref}
        >
          <div>
            <div
              className='bg-white h-5 w-5 cursor-pointer relative overflow-hidden'
              onClick={() => {
                toggle(`${type}-white` as Style, 'remove')
                toggleColors(false)
              }}
            >
              <div className='bg-red-500 w-0.5 h-7 -rotate-45 absolute -top-1 left-2' />
            </div>
            <div
              className='bg-white h-5 w-5 cursor-pointer'
              onClick={() => {
                toggle(`${type}-white` as Style)
                toggleColors(false)
              }}
            />

            <div
              className='bg-black h-5 w-5 cursor-pointer'
              onClick={() => {
                toggle(`${type}-black` as Style)
                toggleColors(false)
              }}
            />
          </div>
          {colors.map((color) => (
            <div key={color}>
              {gradients.map((gradient) => (
                <div
                  key={`${color}-${gradient}`}
                  className={`bg-${color}-${gradient} h-5 w-5 cursor-pointer`}
                  onClick={() => {
                    toggle(`${type}-${color}-${gradient}` as Style)
                    toggleColors(false)
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CellStylingColorPicker
